import { useState, useEffect, useContext } from "react";
import { NavigationContext } from "../../context/NavigationContext";
import {
    Table,
    Card,
    Tag,
    Button,
    Space,
    message,
    Modal,
    Typography,
    Spin,
    Empty,
    Descriptions,
    Divider,
    DatePicker,
    Select,
    Row,
    Col
} from "antd";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    EyeOutlined,
    ClockCircleOutlined,
    RightCircleFilled,
    FilterOutlined,
    ClearOutlined
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function PermissionRequests() {
    const [permissions, setPermissions] = useState([]);
    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [workLocations, setWorkLocations] = useState([]);
    const [createdByUsers, setCreatedByUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [selectedPermission, setSelectedPermission] = useState(null);
    const [viewModalVisible, setViewModalVisible] = useState(false);

    // Filter states
    const [dateRange, setDateRange] = useState([null, null]);
    const [selectedOrganisations, setSelectedOrganisations] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedWorkLocations, setSelectedWorkLocations] = useState([]);
    const [selectedCreatedBy, setSelectedCreatedBy] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    const [employees, setEmployees] = useState([])

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);

    const getEmployees = async () => {
        const res = await axios.get(`${backUrl}/api/emp/employees/?is_hr=true`);
        setEmployees(res.data.results);
        console.log("Data =>", res.data.results)
    }

    useEffect(() => {
        getEmployees()
    }, [])

    const fetchPermissions = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            // Build query parameters
            const params = new URLSearchParams();
            params.append('page', page);
            // params.append('page_size', pageSize);

            // Add date range filters
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.append('from_date', dayjs(dateRange[0]).format('YYYY-MM-DD'));
                params.append('to_date', dayjs(dateRange[1]).format('YYYY-MM-DD'));
            }

            // Add organisation filters
            if (selectedOrganisations.length > 0) {
                selectedOrganisations.forEach(orgId => {
                    params.append('organisation', orgId);
                });
            }

            // Add department filters
            if (selectedDepartments.length > 0) {
                selectedDepartments.forEach(deptId => {
                    params.append('department', deptId);
                });
            }

            // Add work location filters
            if (selectedWorkLocations.length > 0) {
                selectedWorkLocations.forEach(locId => {
                    params.append('location', locId);
                });
            }

            // Add created by filter
            if (selectedCreatedBy) {
                params.append('created_by', selectedCreatedBy);
            }

            // Add status filter
            if (selectedStatus) {
                params.append('status', selectedStatus);
            }

            const res = await axios.get(
                `${backUrl}/api/hr/permission-considerations/?${params.toString()}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` }
                }
            );
            setPermissions(res.data.results || []);
            setPagination({
                current: page,
                pageSize: pageSize,
                total: res.data.count || 0
            });
        } catch (err) {
            message.error("Failed to load permission requests");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setOrganisations(res.data.results || []);
        } catch (err) {
            console.error("Failed to load organisations");
        }
    };

    const fetchDepartments = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setDepartments(res.data.results || []);
        } catch (err) {
            console.error("Failed to load departments");
        }
    };

    const fetchDesignations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setDesignations(res.data.results || []);
        } catch (err) {
            console.error("Failed to load designations");
        }
    };

    const fetchWorkLocations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/worklocation/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setWorkLocations(res.data.results || []);
        } catch (err) {
            console.error("Failed to load work locations");
        }
    };

    useEffect(() => {
        fetchOrganisations();
        fetchDepartments();
        fetchDesignations();
        fetchWorkLocations();
    }, []);

    // ✅ Sync with NavigationContext
    useEffect(() => {
        if (navigationParams?.status) {
            console.log("🎯 PermissionRequests: Syncing status from navigation:", navigationParams.status);
            setSelectedStatus(navigationParams.status.toLowerCase());
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    // ✅ Fetch permissions when filters or search changes
    useEffect(() => {
        fetchPermissions(pagination.current, pagination.pageSize);
    }, [dateRange, selectedOrganisations, selectedDepartments, selectedWorkLocations, selectedCreatedBy, selectedStatus, pagination.current, pagination.pageSize]);

    const getOrgName = (orgId) => {
        const org = organisations.find(o => o.id === orgId);
        return org ? org.name : `Org ${orgId}`;
    };

    const getDeptName = (deptId) => {
        const dept = departments.find(d => d.id === deptId);
        return dept ? dept.name : `Dept ${deptId}`;
    };

    const getDesignationName = (desId) => {
        const des = designations.find(d => d.id === desId);
        return des ? des.name : `Des ${desId}`;
    };

    const getWorkLocationName = (locId) => {
        const loc = workLocations.find(w => w.id === locId);
        return loc ? loc.name : `Loc ${locId}`;   // ✅ return the name field, not the object
    };

    const handleTableChange = (newPagination) => {
        fetchPermissions(newPagination.current, newPagination.pageSize);
    };

    const handleApprove = async (id) => {
        setActionLoading(true);
        try {
            await axios.post(
                `${backUrl}/api/hr/permission-considerations/${id}/approve/`,
                {},
                {
                    headers: { Authorization: `Bearer ${authData?.access}` }
                }
            );
            message.success("Permission request approved successfully");
            fetchPermissions(pagination.current, pagination.pageSize);
        } catch (err) {
            message.error(err.response?.data?.message || "Failed to approve request");
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async (id) => {
        Modal.confirm({
            title: "Reject Permission Request",
            content: "Are you sure you want to reject this permission request?",
            okText: "Reject",
            okType: "danger",
            cancelText: "Cancel",
            onOk: async () => {
                setActionLoading(true);
                try {
                    await axios.post(
                        `${backUrl}/api/hr/permission-considerations/${id}/reject/`,
                        {},
                        {
                            headers: { Authorization: `Bearer ${authData?.access}` }
                        }
                    );
                    message.success("Permission request rejected");
                    fetchPermissions(pagination.current, pagination.pageSize);
                } catch (err) {
                    message.error(err.response?.data?.message || "Failed to reject request");
                } finally {
                    setActionLoading(false);
                }
            }
        });
    };

    const handleViewDetails = (record) => {
        setSelectedPermission(record);
        setViewModalVisible(true);
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        setPagination({ ...pagination, current: 1 }); // Reset to first page
    };

    const handleClearAllFilters = () => {
        setDateRange([null, null]);
        setSelectedOrganisations([]);
        setSelectedDepartments([]);
        setSelectedWorkLocations([]);
        setSelectedCreatedBy(null);
        setSelectedStatus(null);
        setPagination({ ...pagination, current: 1 });
    };

    const hasActiveFilters = () => {
        return (dateRange && dateRange[0] && dateRange[1]) ||
            selectedOrganisations.length > 0 ||
            selectedDepartments.length > 0 ||
            selectedWorkLocations.length > 0 ||
            selectedCreatedBy ||
            selectedStatus;
    };

    const getStatusTag = (status) => {
        const statusMap = {
            pending: { color: "orange", icon: <ClockCircleOutlined /> },
            approved: { color: "green", icon: <CheckCircleOutlined /> },
            rejected: { color: "red", icon: <CloseCircleOutlined /> }
        };
        const config = statusMap[status] || statusMap.pending;
        return (
            <Tag color={config.color} icon={config.icon}>
                {status?.toUpperCase()}
            </Tag>
        );
    };

    const columns = [
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            width: 120,
            render: (date) => dayjs(date).format("DD-MM-YYYY")
        },
        {
            title: "New Start Time",
            dataIndex: "new_start_time",
            key: "new_start_time",
            width: 120,
            render: (time) => time ? dayjs(time, "HH:mm:ss").format("hh:mm A") : "-"
        },
        {
            title: "New End Time",
            dataIndex: "new_end_time",
            key: "new_end_time",
            width: 120,
            render: (time) => time ? dayjs(time, "HH:mm:ss").format("hh:mm A") : "-"
        },
        {
            title: "Applicable for",
            dataIndex: "gender",
            key: "gender",
            width: 100,
            render: (gender) => {
                if (!gender) return <p>ALL</p>;
                return <p color={gender === "male" ? "blue" : "pink"}>{gender.toUpperCase()}</p>;
            }
        },
        {
            title: "Organizations",
            dataIndex: "organisations",
            key: "organisations",
            width: 350,
            render: (orgs) => {
                if (!orgs || orgs.length === 0) return <Text type="secondary">All</Text>;
                return (
                    <Space direction="vertical" size={2}>
                        {orgs.slice(0, 2).map((orgId, idx) => (
                            <p key={idx} color="blue" style={{ fontSize: "11px" }}>
                                {getOrgName(orgId)}
                            </p>
                        ))}
                        {orgs.length > 2 && (
                            <Text type="secondary" style={{ fontSize: "11px" }}>
                                +{orgs.length - 2} more
                            </Text>
                        )}
                    </Space>
                );
            }
        },
        {
            title: "Departments",
            dataIndex: "departments",
            key: "departments",
            width: 350,
            render: (depts) => {
                if (!depts || depts.length === 0) return <Text type="secondary">-</Text>;
                return (
                    <Space direction="vertical" size={2}>
                        {depts.map((deptId, idx) => (
                            <p key={idx} color="cyan" style={{ fontSize: "11px" }}>
                                {getDeptName(deptId)}
                            </p>
                        ))}
                    </Space>
                );
            }
        },
        {
            title: "Work Locations",
            dataIndex: "work_locations",
            key: "work_locations",
            width: 350,
            render: (locationIds) => {
                if (!locationIds || locationIds.length === 0) return <Text type="secondary">-</Text>;
                return (
                    <Space direction="vertical" size={2}>
                        {locationIds.map((locId, idx) => (
                            <p key={idx} color="geekblue" style={{ fontSize: "11px" }}>
                                {getWorkLocationName(locId)}
                            </p>
                        ))}
                    </Space>
                );
            }
        },
        {
            title: "Created By",
            dataIndex: "created_by_name",
            key: "created_by_name",
            width: 170,
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            width: 200,
            render: (time) =>
                time
                    ? dayjs(time).format("DD/MM/YYYY hh:mm A")
                    : "-"
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status) => getStatusTag(status)
        },
        authData?.employee?.is_superuser && {
            title: "Actions",
            key: "actions",
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        size="small"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleApprove(record.id)}
                        disabled={record.status !== 'pending'}
                        loading={actionLoading}
                    >
                        Approve
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => handleReject(record.id)}
                        disabled={record.status !== 'pending'}
                        loading={actionLoading}
                    >
                        Reject
                    </Button>
                </Space>
            )
        }
    ].filter(Boolean);

    return (
        <div style={{
            background: "#ffffffff",
            minHeight: "100vh",
            padding: "24px"
        }}>
            <div style={{ maxWidth: "1600px", margin: "0 auto" }}>
                <div style={{
                    marginBottom: "24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <Title level={4} style={{ margin: 0, color: "#0e497b" }}>
                        Permission Requests
                    </Title>
                </div>

                {/* Filter Section */}
                <Card
                    style={{ marginBottom: "24px" }}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Date Range</Text>
                                <RangePicker
                                    value={dateRange}
                                    onChange={handleDateRangeChange}
                                    format="DD-MM-YYYY"
                                    placeholder={["Start Date", "End Date"]}
                                    style={{ width: "100%" }}
                                />
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Organizations</Text>
                                <Select
                                    mode="multiple"
                                    placeholder="Select organizations"
                                    value={selectedOrganisations}
                                    onChange={(value) => {
                                        setSelectedOrganisations(value);
                                        setPagination({ ...pagination, current: 1 });
                                    }}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {organisations.map(org => (
                                        <Option key={org.id} value={org.id}>
                                            {org.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Departments</Text>
                                <Select
                                    mode="multiple"
                                    placeholder="Select departments"
                                    value={selectedDepartments}
                                    onChange={(value) => {
                                        setSelectedDepartments(value);
                                        setPagination({ ...pagination, current: 1 });
                                    }}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {departments.map(dept => (
                                        <Option key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Work Locations</Text>
                                <Select
                                    mode="multiple"
                                    placeholder="Select work locations"
                                    value={selectedWorkLocations}
                                    onChange={(value) => {
                                        setSelectedWorkLocations(value);
                                        setPagination({ ...pagination, current: 1 });
                                    }}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {workLocations.map(loc => (
                                        <Option key={loc.id} value={loc.id}>
                                            {loc.name}   {/* ✅ render the name, not the whole object */}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Status</Text>
                                <Select
                                    placeholder="Select status"
                                    value={selectedStatus}
                                    onChange={(value) => {
                                        setSelectedStatus(value);
                                        setPagination({ ...pagination, current: 1 });
                                    }}
                                    style={{ width: "100%" }}
                                    allowClear
                                >
                                    <Option value="pending">Pending</Option>
                                    <Option value="approved">Approved</Option>
                                    <Option value="rejected">Rejected</Option>
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Created By</Text>
                                <Select
                                    placeholder="Select creator"
                                    value={selectedCreatedBy}
                                    onChange={(value) => {
                                        setSelectedCreatedBy(value);
                                        setPagination({ ...pagination, current: 1 });
                                    }}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {employees.map(user => (
                                        <Option key={user?.user?.id} value={user?.user?.id}>
                                            {user?.user?.first_name}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                <Card>
                    <Table
                        columns={columns}
                        dataSource={permissions}
                        rowKey="id"
                        loading={loading}
                        pagination={pagination}
                        onChange={handleTableChange}
                        scroll={{ x: 2000 }}
                        locale={{
                            emptyText: (
                                <Empty
                                    description="No permission requests found"
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            )
                        }}
                        components={{
                            header: {
                                cell: (props) => (
                                    <th {...props} style={{ backgroundColor: "#0e497b", color: 'white' }}>
                                        {props.children}
                                    </th>
                                ),
                            },
                        }}
                        bordered
                    />
                </Card>
            </div>

            {/* View Details Modal */}
            <Modal
                title={
                    <Title level={4} style={{ margin: 0, color: "#0e497b" }}>
                        Permission Request Details
                    </Title>
                }
                open={viewModalVisible}
                onCancel={() => setViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setViewModalVisible(false)}>
                        Close
                    </Button>,
                    selectedPermission?.status === "pending" && authData?.employee?.is_superuser && (
                        <Button
                            key="approve"
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            onClick={() => {
                                handleApprove(selectedPermission.id);
                                setViewModalVisible(false);
                            }}
                            loading={actionLoading}
                            style={{ background: "#52c41a", borderColor: "#52c41a" }}
                        >
                            Approve
                        </Button>
                    ),
                    selectedPermission?.status === "pending" && authData?.employee?.is_superuser && (
                        <Button
                            key="reject"
                            danger
                            icon={<CloseCircleOutlined />}
                            onClick={() => {
                                handleReject(selectedPermission.id);
                                setViewModalVisible(false);
                            }}
                            loading={actionLoading}
                        >
                            Reject
                        </Button>
                    )
                ]}
                width={700}
            >
                {selectedPermission && (
                    <div>
                        <Descriptions column={2} bordered size="small">
                            <Descriptions.Item label="Date" span={1}>
                                {dayjs(selectedPermission.date).format("DD-MM-YYYY")}
                            </Descriptions.Item>
                            <Descriptions.Item label="New Start Time" span={1}>
                                {selectedPermission.new_start_time
                                    ? dayjs(selectedPermission.new_start_time, "HH:mm:ss").format("hh:mm A")
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Gender" span={2}>
                                {selectedPermission.gender ? (
                                    <Tag color={selectedPermission.gender === "male" ? "blue" : "pink"}>
                                        {selectedPermission.gender.toUpperCase()}
                                    </Tag>
                                ) : (
                                    <Tag>ALL</Tag>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Status" span={2}>
                                {getStatusTag(selectedPermission.status)}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider />

                        <Title level={5} style={{ color: "#0e497b" }}>Filter Criteria</Title>

                        {selectedPermission.organisations && selectedPermission.organisations.length > 0 && (
                            <div style={{ marginBottom: "16px" }}>
                                <Text strong>Organizations:</Text>
                                <div style={{ marginTop: "8px" }}>
                                    {selectedPermission.organisations.map((orgId, idx) => (
                                        <Tag key={idx} color="blue" style={{ marginBottom: "4px" }}>
                                            {getOrgName(orgId)}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedPermission.departments && selectedPermission.departments.length > 0 && (
                            <div style={{ marginBottom: "16px" }}>
                                <Text strong>Departments:</Text>
                                <div style={{ marginTop: "8px" }}>
                                    {selectedPermission.departments.map((deptId, idx) => (
                                        <Tag key={idx} color="cyan" style={{ marginBottom: "4px" }}>
                                            {getDeptName(deptId)}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedPermission.designations && selectedPermission.designations.length > 0 && (
                            <div style={{ marginBottom: "16px" }}>
                                <Text strong>Designations:</Text>
                                <div style={{ marginTop: "8px" }}>
                                    {selectedPermission.designations.map((desId, idx) => (
                                        <Tag key={idx} color="purple" style={{ marginBottom: "4px" }}>
                                            {getDesignationName(desId)}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedPermission.work_locations && selectedPermission.work_locations.length > 0 && (
                            <div style={{ marginBottom: "16px" }}>
                                <Text strong>Work Locations:</Text>
                                <div style={{ marginTop: "8px" }}>
                                    {selectedPermission.work_locations.map((locId, idx) => (
                                        <Tag key={idx} color="geekblue" style={{ marginBottom: "4px" }}>
                                            {getWorkLocationName(locId)}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedPermission.reason && (
                            <>
                                <Divider />
                                <div>
                                    <Text strong>Reason:</Text>
                                    <div style={{
                                        marginTop: "8px",
                                        padding: "12px",
                                        background: "#f5f7fa",
                                        borderRadius: "6px",
                                        border: "1px solid #e8eef5"
                                    }}>
                                        <Text>{selectedPermission.reason}</Text>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}